import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Lock, ShieldCheck, RotateCcw, Save, Loader2, ArrowLeft, SlidersHorizontal, ClipboardList } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/quote/Header";
import ZipRateManager from "@/components/quote/ZipRateManager";
import { adminLogin, getAdminMetros, saveAdminMetro, resetAdminMetro, getAdminSettings, saveAdminSettings } from "@/lib/api";

const FIELDS = [
  { k: "storage", label: "Storage $/sqft", step: 0.05 },
  { k: "labor", label: "Labor $/hr", step: 0.25 },
  { k: "inbound", label: "Inbound $/plt", step: 0.25 },
  { k: "outbound", label: "Outbound $/plt", step: 0.25 },
];

const PC_KEY = "gg_admin_pc";

export default function RateAdmin() {
  const navigate = useNavigate();
  const [passcode, setPasscode] = useState(sessionStorage.getItem(PC_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [metros, setMetros] = useState([]);
  const [uplift, setUplift] = useState(10);
  const [draft, setDraft] = useState({});
  const [busyKey, setBusyKey] = useState("");
  const [settings, setSettings] = useState({ laborUplift: 10, quoteValidityDays: 30 });
  const [savingSettings, setSavingSettings] = useState(false);

  const hydrate = (list) => {
    setMetros(list);
    const d = {};
    list.forEach((m) => (d[m.key] = { ...m.current }));
    setDraft(d);
  };

  const loadMetros = async (pc) => {
    const data = await getAdminMetros(pc);
    hydrate(data.metros);
    if (data.laborUplift != null) setUplift(data.laborUplift);
    try {
      const s = await getAdminSettings(pc);
      setSettings(s);
      setUplift(s.laborUplift);
    } catch (e) {
      console.warn("Admin settings load failed", e);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const s = await saveAdminSettings(passcode, {
        laborUplift: +settings.laborUplift,
        quoteValidityDays: +settings.quoteValidityDays,
      });
      setSettings({ laborUplift: s.laborUplift, quoteValidityDays: s.quoteValidityDays });
      setUplift(s.laborUplift);
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Could not save settings", { description: e?.response?.data?.detail || "Try again." });
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    document.title = "GloGrid · Rate Admin";
    const stored = sessionStorage.getItem(PC_KEY);
    if (stored) {
      loadMetros(stored)
        .then(() => setAuthed(true))
        .catch(() => {
          sessionStorage.removeItem(PC_KEY);
          setPasscode("");
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogin = async (e) => {
    e.preventDefault();
    setChecking(true);
    try {
      await adminLogin(passcode);
      sessionStorage.setItem(PC_KEY, passcode);
      await loadMetros(passcode);
      setAuthed(true);
      toast.success("Welcome to Rate Admin");
    } catch (e) {
      toast.error("Invalid passcode");
    } finally {
      setChecking(false);
    }
  };

  const setField = (key, f, v) =>
    setDraft((p) => ({ ...p, [key]: { ...p[key], [f]: v === "" ? "" : Math.max(0, +v) } }));

  const save = async (m) => {
    setBusyKey(m.key);
    try {
      const d = draft[m.key];
      await saveAdminMetro(passcode, { key: m.key, ...d });
      toast.success(`${m.city} rates saved`);
      await loadMetros(passcode);
    } catch (e) {
      toast.error("Could not save", { description: e?.response?.data?.detail || "Try again." });
    } finally {
      setBusyKey("");
    }
  };

  const reset = async (m) => {
    setBusyKey(m.key);
    try {
      await resetAdminMetro(passcode, m.key);
      toast.success(`${m.city} reset to default`);
      await loadMetros(passcode);
    } catch (e) {
      toast.error("Could not reset");
    } finally {
      setBusyKey("");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(PC_KEY);
    setAuthed(false);
    setPasscode("");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-slate-100">
        <Header />
        <main className="mx-auto flex max-w-md flex-col px-6 py-20">
          <div className="rounded-sm border border-slate-200 bg-white p-8 shadow-sm gg-rise">
            <div className="grid h-11 w-11 place-items-center rounded-sm bg-slate-900 text-white">
              <Lock className="h-5 w-5" />
            </div>
            <h1 className="mt-5 font-heading text-2xl font-extrabold tracking-tight text-slate-900">Rate Admin</h1>
            <p className="mt-2 text-sm text-slate-500">
              Enter the shared admin passcode to manage city storage and labor rates.
            </p>
            <form onSubmit={onLogin} className="mt-6 flex flex-col gap-3">
              <Label htmlFor="pc" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Passcode
              </Label>
              <Input
                id="pc"
                type="password"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="••••••••"
                className="h-12 rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                data-testid="admin-passcode-input"
              />
              <Button
                type="submit"
                disabled={checking || !passcode}
                className="h-12 rounded-sm bg-slate-900 font-semibold text-white transition-colors hover:bg-blue-600 disabled:opacity-50"
                data-testid="admin-login-btn"
              >
                {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}
              </Button>
            </form>
          </div>
          <button
            onClick={() => navigate("/")}
            className="mt-6 inline-flex items-center gap-1.5 self-start text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" /> Back to calculator
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-10 md:px-8">
        <div className="flex items-end justify-between">
          <div>
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-green-600">
              <ShieldCheck className="h-4 w-4" /> Admin session
            </div>
            <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900">
              City Rate Management
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Edit per-city <strong>basic</strong> rates. Saved changes override the built-in benchmarks and apply to
              new quotes instantly. Customer-facing quotes add a standard ${uplift.toFixed(0)}/hr labor loading on top of the labor rate shown here.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/sales"
              className="inline-flex items-center gap-1.5 rounded-sm bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
              data-testid="admin-to-sales"
            >
              <ClipboardList className="h-4 w-4" /> Sales Dashboard
            </Link>
            <Button
              variant="outline"
              onClick={logout}
              className="rounded-sm border-slate-300 font-semibold text-slate-700 hover:bg-slate-100"
              data-testid="admin-logout-btn"
            >
              Lock
            </Button>
          </div>
        </div>

        {/* Global settings */}
        <div className="mt-8 rounded-sm border border-slate-200 bg-white p-6 shadow-sm" data-testid="admin-settings">
          <div className="mb-4 flex items-center gap-2.5">
            <span className="grid h-7 w-7 place-items-center rounded-sm bg-slate-900 text-white">
              <SlidersHorizontal className="h-4 w-4" />
            </span>
            <h2 className="font-heading text-xl font-extrabold tracking-tight text-slate-900">Global Settings</h2>
          </div>
          <div className="flex flex-wrap items-end gap-6">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Labor loading ($/hr)</Label>
              <Input
                type="number"
                min={0}
                step={0.5}
                value={settings.laborUplift}
                onChange={(e) => setSettings((p) => ({ ...p, laborUplift: e.target.value }))}
                className="h-11 w-40 rounded-sm border-slate-300 font-mono-plex focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                data-testid="settings-uplift"
              />
              <span className="text-xs text-slate-400">Added on top of every basic labor rate</span>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Quote validity (days)</Label>
              <Input
                type="number"
                min={1}
                step={1}
                value={settings.quoteValidityDays}
                onChange={(e) => setSettings((p) => ({ ...p, quoteValidityDays: e.target.value }))}
                className="h-11 w-40 rounded-sm border-slate-300 font-mono-plex focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                data-testid="settings-validity"
              />
              <span className="text-xs text-slate-400">How long new quotes stay valid</span>
            </div>
            <Button
              onClick={saveSettings}
              disabled={savingSettings}
              className="h-11 rounded-sm bg-slate-900 px-5 font-semibold text-white transition-colors hover:bg-blue-600"
              data-testid="settings-save-btn"
            >
              {savingSettings ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save settings
            </Button>
          </div>
        </div>

        <div className="mt-8 overflow-x-auto rounded-sm border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[820px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">City</th>
                {FIELDS.map((f) => (
                  <th key={f.k} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {f.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {metros.map((m) => (
                <tr key={m.key} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`admin-row-${m.city}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{m.city}, {m.state}</div>
                    {m.overridden && (
                      <span className="mt-0.5 inline-block rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                        Custom
                      </span>
                    )}
                  </td>
                  {FIELDS.map((f) => (
                    <td key={f.k} className="px-4 py-3">
                      <Input
                        type="number"
                        min={0}
                        step={f.step}
                        value={draft[m.key]?.[f.k] ?? ""}
                        onChange={(e) => setField(m.key, f.k, e.target.value)}
                        className="h-9 w-24 rounded-sm border-slate-300 font-mono-plex text-sm focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                        data-testid={`admin-input-${m.city}-${f.k}`}
                      />
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        onClick={() => save(m)}
                        disabled={busyKey === m.key}
                        className="h-9 rounded-sm bg-slate-900 px-3 font-semibold text-white transition-colors hover:bg-blue-600"
                        data-testid={`admin-save-${m.city}`}
                      >
                        {busyKey === m.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => reset(m)}
                        disabled={busyKey === m.key || !m.overridden}
                        className="h-9 rounded-sm border-slate-300 px-3 text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-40"
                        data-testid={`admin-reset-${m.city}`}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ZipRateManager passcode={passcode} laborUplift={uplift} />
      </main>
    </div>
  );
}
